/**
 * Permissive duration parser. Returns minutes as integer, or null when input
 * can't be understood.
 *
 * Accepts:
 *   "90"            → 90
 *   "1h"            → 60
 *   "1h 30m"        → 90
 *   "1h30"          → 90
 *   "2h"            → 120
 *   "45m"           → 45
 *   "1.5h"          → 90
 *   "2:30" / "2:30h" → 150  (hours:minutes form)
 *   "0:45"          → 45
 */
export function parseDuration(raw) {
  if (raw == null) return null;
  let s = String(raw).trim().toLowerCase().replace(/\s+/g, "");
  if (!s) return null;

  // h:mm form e.g. "2:30" or "2:30h"
  if (s.includes(":")) {
    s = s.replace(/h$/, "");
    const [hStr, mStr] = s.split(":");
    const h = parseFloat(hStr);
    const m = parseFloat(mStr);
    if (Number.isFinite(h) && Number.isFinite(m)) return Math.round(h * 60 + m);
    return null;
  }

  // Compound "1h30m" / "1h30" / "1h 30m" — the regex pulls hours then minutes.
  const compoundMatch = s.match(/^(\d+(?:\.\d+)?)h(\d+(?:\.\d+)?)?m?$/);
  if (compoundMatch) {
    const h = parseFloat(compoundMatch[1]);
    const m = parseFloat(compoundMatch[2] || "0");
    return Math.round(h * 60 + m);
  }

  // Pure hours: "2h" / "1.5h"
  const hOnly = s.match(/^(\d+(?:\.\d+)?)h$/);
  if (hOnly) return Math.round(parseFloat(hOnly[1]) * 60);

  // Pure minutes: "45m" / "30m"
  const mOnly = s.match(/^(\d+(?:\.\d+)?)m$/);
  if (mOnly) return Math.round(parseFloat(mOnly[1]));

  // Bare number → assume minutes.
  const num = parseFloat(s);
  if (Number.isFinite(num)) return Math.round(num);
  return null;
}

/** Renders minutes as a compact "1h 30m" / "45m" / "2h" string. */
export function formatDuration(min) {
  if (min == null || !Number.isFinite(min)) return "—";
  const m = Math.max(0, Math.round(min));
  if (m === 0) return "0m";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
}
